import { useState } from "react";
import { api, type RelayServer } from "../lib/api.js";
import { relativeTime } from "../lib/format.js";
import { Empty, Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

export default function Relays({ me, onToggleRail }: PageProps) {
  const relays = useAsync(() => api.get<RelayServer[]>("/api/console/relay-servers"), []);
  const [host, setHost] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState("");
  const canWrite = me.permissions.includes("*") || me.permissions.includes("relay:write");

  async function add() {
    setError("");
    try {
      await api.post("/api/console/relay-servers", { host, region, weight: 1 });
      setHost(""); setRegion(""); relays.reload();
    } catch (e) { setError((e as Error).message); }
  }

  async function patch(r: RelayServer, body: Partial<RelayServer>) {
    try { await api.patch(`/api/console/relay-servers/${r.id}`, body); relays.reload(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Relays</h1>
          <span className="topbar__hint">{relays.data?.length ?? 0} configured</span>
        </div>
      </header>

      <div className="content">
        <div className="stack">
          {error ? <Notice>{error}</Notice> : null}

          <div className="notice">
            Relays carry traffic when two machines cannot reach each other directly, and always
            carry browser sessions. Add more to spread load or to place one near a site.
            The rendezvous server picks up changes within 30 seconds.
          </div>

          {!relays.loading && !relays.data?.length ? (
            <Empty
              title="No relays configured"
              hint="The rendezvous server falls back to whatever was passed on its command line. Add a relay here to manage the pool from the console."
            />
          ) : (
            <div className="panel">
              <div className="tablewrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Host</th><th>Region</th><th className="num">Weight</th>
                      <th>Status</th><th>Last seen</th><th style={{ width: 150 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(relays.data ?? []).map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{r.host}</td>
                        <td className="muted">{r.region || <span className="faint">&mdash;</span>}</td>
                        <td className="num">{r.weight}</td>
                        <td>
                          <span className={`tag ${r.enabled ? "tag--live" : ""}`}>
                            {r.enabled ? "In use" : "Disabled"}
                          </span>
                        </td>
                        <td className="small faint">{r.last_seen ? relativeTime(r.last_seen) : "—"}</td>
                        <td className="actions">
                          {canWrite ? (
                            <>
                              <button className="btn btn--sm" onClick={() => patch(r, { enabled: r.enabled ? 0 : 1 } as any)}>
                                {r.enabled ? "Disable" : "Enable"}
                              </button>
                              <button className="btn btn--danger btn--sm"
                                onClick={() => api.del(`/api/console/relay-servers/${r.id}`).then(relays.reload)}>
                                Remove
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {canWrite ? (
            <div className="panel">
              <div className="panel__head"><h2>Add a relay</h2></div>
              <div className="panel__body">
                <div className="row row--wrap" style={{ alignItems: "flex-end" }}>
                  <div className="field" style={{ marginBottom: 0, flex: "2 1 240px" }}>
                    <label className="field__label">Host and port</label>
                    <input className="mono" value={host} onChange={(e) => setHost(e.target.value)}
                      placeholder="relay.example.com:21117" />
                    <span className="field__hint">Port 21117 is assumed if you leave it off.</span>
                  </div>
                  <div className="field" style={{ marginBottom: 0, flex: "1 1 160px" }}>
                    <label className="field__label">Region</label>
                    <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="eu-west" />
                  </div>
                  <button className="btn btn--primary" onClick={add} disabled={!host.trim()}>Add relay</button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
